import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import * as client from "../../../client";
import styles from "./ColumnEditModal.module.css";
import { getAddressColumns, SovColumnProps } from "./AddressCardColumns";
import { useClientId } from "../../../ClientConfig";
import { SafeReportResponse } from "../Conversion";
import { useAddManualAddress } from "../data";
import { Panel } from "primereact/panel";
import { OneLineStringEditor } from "./CellEditors";

const DEFAULT_ADDRESS: Partial<client.RawAddress> = {
  country_code: "US",
  bpp_value: "0",
  bi_excl_ee_value: "0",
  building_value: "0",
};

export const AddAddressModal = ({
  report,
  visible,
  setVisible,
  additionalStyles,
  lob,
}: {
  report: SafeReportResponse;
  visible: boolean;
  setVisible: (value: boolean) => void;
  additionalStyles: React.CSSProperties;
  lob: string;
}) => {
  const clientId = useClientId();
  const columns = getAddressColumns(lob)[clientId];
  // Use a single address object for all field values
  const [address, setAddress] =
    useState<Partial<client.RawAddress>>(DEFAULT_ADDRESS);
  const addManualAddress = useAddManualAddress(report.report_id);

  if (!columns) {
    return null;
  }

  const handleFieldChange = (
    field: keyof client.RawAddress,
    value: string | number
  ) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const renderColumn = (col: SovColumnProps, isRequired: boolean) => {
    if (!col.editor || !col.field) {
      return null;
    }

    // TIV is special in this modal: we don't want the user to set it, but we
    // want it to be present to make it explicit that it is derived.
    const EditorComponent =
      col.field === "tiv" ? OneLineStringEditor : col.editor;
    const field = col.field;
    const value = address[field] ?? "";

    return (
      <div className={styles.pairedLabelAndBox} key={field}>
        <label className={styles.fieldLabel}>
          <strong>{col.columnName}</strong>
          {isRequired ? "*" : null}
        </label>
        <EditorComponent
          options={{
            value:
              col.field === "tiv"
                ? "This will be calculated based on other coverages"
                : value,
            editorCallback: (val: string | number) => {
              handleFieldChange(field, val);
            },
          }}
          disabled={col.field === "tiv"}
          fieldType={"string"}
          additionalStyles={additionalStyles}
          isUnitedStates={address.country_code === "US"}
        />
      </div>
    );
  };

  const requiredColumns = columns.filter((col) => col.requiredForValidAddress);
  const optionalColumns = columns.filter((col) => !col.requiredForValidAddress);

  function isCompleteRawAddress(
    addr: Partial<client.RawAddress>
  ): addr is client.RawAddress {
    return requiredColumns.every((col) => {
      if (!col.field || col.field === "tiv") {
        return true;
      }
      const v = addr[col.field];
      return (
        v !== undefined &&
        v !== null &&
        v !== "" &&
        !(typeof v === "number" && isNaN(v))
      );
    });
  }

  const handleSave = async () => {
    if (!isCompleteRawAddress(address)) return;

    // We need to set some TIV for validation, but it will be recalculated on the backend:
    await addManualAddress.mutateAsync({ ...address, tiv: 0.0 });

    setVisible(false);
    setAddress(DEFAULT_ADDRESS);
  };

  return (
    <Dialog
      header="Add New Location"
      className={styles.dialog}
      visible={visible}
      onHide={() => {
        setVisible(false);
        setAddress(DEFAULT_ADDRESS);
      }}
      modal
      footer={
        <div className={styles.dialogFooter}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={() => {
              setVisible(false);
              setAddress(DEFAULT_ADDRESS);
            }}
            severity="secondary"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            onClick={handleSave}
            loading={addManualAddress.isPending}
            disabled={
              !isCompleteRawAddress(address) || addManualAddress.isPending
            }
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <div>
          {requiredColumns
            .filter((col) => col.field && col.editor)
            .map((col) => renderColumn(col, true))}
        </div>
        <Panel header="Other Fields" toggleable={true} collapsed={true}>
          {optionalColumns
            .filter((col) => col.field && col.editor)
            .map((col) => renderColumn(col, false))}
        </Panel>
      </div>
    </Dialog>
  );
};
